import readFile from './read_file'
import { show, hide } from '../../util/show'

/*
FileUpload helps you to show nice file-upload dialogues.

Steps to do so:
  - All stuff needs to be in a wrapper element with the class "js-file_upload_container"
  - Hide the file-input with CSS and add the class ".js-file_upload" to it
  - Add a label and style it like you want. This label will be the element the user
    will click to upload an image.

  - If you want to show a preview image BEFORE upload happens (FileReader API) add
    an element with the class ".js-file_upload_preview_container" into the new label. It's backgroundImage
    will be set to the file that will be uploaded. Style with CSS (background-size: cover, ...).
    All other elements within that element will be deleted (think of info texts, ...).
    If you want to include the preview outside of the wrapper with class "js-file_upload_container",
    put the selector of the preview container on the "js-file_upload" element
    in a data attribute with the name "file_upload_preview_container_selector
*/
export default class FileUpload {
  savedFileInputFiles
  // element is a .js-file_upload element
  constructor(element) {
    this.init(element)
  }

  init(element) {
    element.addEventListener('change', this.fileInputChanged.bind(this))
    this.getDeleteButton(element)?.addEventListener('click', this.deleteClicked.bind(this))

    // .js-file_upload_label is a fake label (div) so we need to handle its click events and pass them on to the input
    this.getWrapper(element)
      .querySelector('.js-file_upload_label')
      .addEventListener('click', e => {
        e.preventDefault()

        element.dispatchEvent(
          new MouseEvent('click', {
            view: window,
            bubbles: true
          })
        )
      })
  }

  fileInputChanged(e) {
    const fileInput = e.target
    if (!fileInput.files[0]) {
      this.restoreSuccessfulInputFiles(fileInput)
    } else {
      readFile(fileInput, this.readFileSuccess.bind(this), this.readFileError.bind(this))
    }
  }

  readFileSuccess(fileInput, file, base64File) {
    this.saveSuccessfulInputFiles(fileInput)
    this.getImagePreviewContainer(fileInput).style.backgroundImage = 'url(' + base64File + ')'
    const noFileChosenHint = this.getNoFileChosenHint(fileInput)
    if (noFileChosenHint) {
      hide(noFileChosenHint)
    }

    const deleteButton = this.getDeleteButton(fileInput)
    if (deleteButton) {
      show(deleteButton)
    }

    this.getDeleteImageInput(fileInput)?.removeAttribute('name')
  }

  readFileError(fileInput) {
    fileInput.value = ''
  }

  deleteClicked(e) {
    const target = e.target

    const defaultImage = this.getImagePreviewContainer(target).dataset.defaultImage
    if (defaultImage) {
      this.getImagePreviewContainer(target).style.backgroundImage = 'url(' + defaultImage + ')'
    } else {
      this.getImagePreviewContainer(target).style.backgroundImage = ''
    }

    const noFileChosenHint = this.getNoFileChosenHint(target)
    if (noFileChosenHint) {
      show(noFileChosenHint)
    }

    const deleteButton = this.getDeleteButton(target)
    if (deleteButton) {
      hide(deleteButton)
    }

    this.getFileInput(target).value = ''
    this.getDeleteImageInput(target)?.setAttribute('name', this.getDeleteImageInput(target).dataset.inputName)
    return false
  }

  saveSuccessfulInputFiles(fileInput) {
    this.savedFileInputFiles = fileInput.files
  }

  restoreSuccessfulInputFiles(fileInput) {
    fileInput.files = this.savedFileInputFiles
  }

  getWrapper(element) {
    return element.closest('.js-file_upload_container')
  }

  getImagePreviewContainer(element) {
    const selector = element.dataset.fileUploadPreviewContainerSelector
    if (selector) {
      return document.querySelector(selector)
    } else {
      return this.getWrapper(element).querySelector('.js-file_upload_preview_container')
    }
  }

  getNoFileChosenHint(element) {
    return this.getWrapper(element).querySelector('.js-file_upload_no_file_chosen_hint')
  }

  getDeleteButton(element) {
    return this.getWrapper(element).querySelector('.js-file_upload_delete_button')
  }

  getDeleteImageInput(element) {
    return this.getWrapper(element).querySelector('.js-file_upload_delete_image_input')
  }

  getFileInput(element) {
    return this.getWrapper(element).querySelector('.js-file_upload')
  }
}
